/**
 * AVA-475 — resolve the support's selected reference drawing into something the
 * report can print as a page.
 *
 * TWO FORMATS RENDER: raster images, and single-page PDFs.
 *
 * Reference documents are displayed by the shared viewer in
 * `Documents/viewer/` (an `<img>`, a pdf.js PDFViewer, or an external Office
 * viewer). NONE of those is reusable here — a viewer renders pages into the
 * screen, not into a printable raster, and it is shared with other features. An earlier
 * revision concluded from that that PDFs were infeasible. THAT WAS WRONG, and
 * the reason is worth keeping: it reasoned about reusing the existing VIEWER
 * rather than about the underlying capability. ISO sheets on this project are
 * predominantly single-page PDFs, so "skip every PDF" made the feature inert on
 * exactly the data it exists for.
 *
 * PDFs are now rasterised directly with `pdfjs-dist`, LAZILY imported so the
 * library only reaches the browser when a PDF is actually attached — it is far
 * too large to sit in the main bundle for a feature most sessions never use.
 *
 * SINGLE PAGE IS NOW MEASURED, NOT ASSUMED. A raster image is one page by
 * definition; a PDF is asked (`numPages`). Multi-page references are still
 * skipped, and that skip finally means what it says.
 *
 * WORKER: pdf.js runs its parser on the MAIN THREAD here, deliberately. Loading
 * `pdf.worker.entry` sets `globalThis.pdfjsWorker`, which pdf.js checks before
 * it will spawn a real Worker (verified in the installed build: pdf.js:4402
 * guards on `_mainThreadWorkerMessageHandler`, :4537 reads that global). The
 * alternative — a real Worker via `new URL(..., import.meta.url)` — needs the
 * bundler to emit the worker as an asset, and `import.meta` is a hard syntax
 * error inside the node assertion harness that covers this file. One ISO sheet
 * costs a few hundred milliseconds of main thread while the preview already
 * shows "Preparing…", which is a fair trade for wiring that cannot silently
 * fail to resolve at runtime. If that hitch ever matters, the upgrade is to
 * copy the worker into `public/` from node_modules at build time (keeping the
 * versions locked together) and set `workerSrc` to that plain URL.
 */
import { log as debugLog, warn as debugWarn } from '@/utils/debugLogger';

/** Why a drawing was not attached. Logged, never surfaced as an error. */
export const DRAWING_SKIP = Object.freeze({
  NONE_SELECTED: 'no reference drawing selected',
  NO_SOURCE: 'document has no retrievable file',
  UNSUPPORTED_FORMAT: 'not a raster image or a PDF — Office and other formats cannot be rendered',
  MULTI_PAGE: 'document has more than one page — only single-page references can be attached',
  UNREADABLE: 'file could not be retrieved or decoded',
});

/**
 * Raster formats. Mirrors DocumentViewer's `supportedImageExtensions` — that
 * component owns the convention for what this app considers an image, and the
 * two must not disagree about a given file.
 *
 * EXPORTED so `drawingPopout.js` can decide what a pop-out window is able to
 * render without keeping a THIRD copy of this list (DocumentViewer holds the
 * second). Whatever the report will attach, the pop-out can show.
 */
export const IMAGE_EXTENSIONS = Object.freeze([
  'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico', 'tiff', 'apng', 'avif',
]);

/**
 * Formats jsPDF embeds directly. Anything else is re-encoded, which is safe
 * because a data-URL-sourced image never taints a canvas.
 */
const PASSTHROUGH_MIME = Object.freeze({
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
});

/** Exported alongside IMAGE_EXTENSIONS, for the same reason. */
export const PDF_EXTENSION = 'pdf';

/** Long edge above which the drawing is re-encoded, to keep the PDF sane. */
const MAX_LONG_EDGE = 2200;
const REENCODE_QUALITY = 0.9;

/**
 * Rasterisation target for a PDF page. An ISO sheet is read at a zoom, so it
 * needs more pixels than it will ever be printed at; 2000 keeps line weights
 * and dimension text legible without making the embedded JPEG absurd.
 */
const PDF_TARGET_LONG_EDGE = 2000;
const PDF_QUALITY = 0.9;

/** Our reference to v3's WorkerMessageHandler, captured on first load. */
let v3WorkerHandle = null;

/**
 * Lazily pull in pdf.js and its main-thread worker handler.
 *
 * BOTH IMPORTS SHARE ONE CHUNK NAME so the library and its worker arrive
 * together in a single code-split request, and neither reaches the main bundle.
 * The worker entry is imported for its SIDE EFFECT — it assigns
 * `globalThis.pdfjsWorker`, which is what lets pdf.js run without fetching a
 * separate worker file (see the module note on WORKER).
 *
 * That global is SHARED with the pdf.js 2.4.456 the app loads from a CDN, so we
 * do not leave it set — see rasterizeFirstPdfPage for the borrow/return.
 */
async function loadPdfjs() {
  const [pdfjs] = await Promise.all([
    import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/legacy/build/pdf'),
    import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/legacy/build/pdf.worker.entry'),
  ]);
  // The worker entry's `globalThis.pdfjsWorker = ...` is a ONE-TIME ES module
  // side effect. Because we hand that global back after every render (see
  // rasterizeFirstPdfPage), we have to keep our own reference — a second render
  // re-imports a cached module whose side effect will not run again.
  if (!v3WorkerHandle) v3WorkerHandle = globalThis.pdfjsWorker;
  return pdfjs;
}

/**
 * Put a borrowed global back exactly as we found it — including "absent".
 * Assigning `undefined` is not the same as never having defined the key:
 * pdf.js 2.4.456 guards with `globalThis.pdfjsWorker && ...`, but a stray own
 * property still shows up to anything using `in` or `hasOwnProperty`.
 */
function restoreGlobal(key, previous) {
  if (previous === undefined) delete globalThis[key];
  else globalThis[key] = previous;
}

/**
 * Caption length cap.
 *
 * A document record is EXTERNAL DATA and its name fields are not guaranteed to
 * be short — nothing in the API contract stops a `documentTitle` carrying a
 * whole path, or a caller passing something stranger. The caption is a single
 * line on the drawing page, so anything past this is unprintable by definition,
 * and clamping at the boundary keeps unbounded input away from the layout's
 * text measurement entirely.
 */
const MAX_NAME_CHARS = 200;

function safeName(...candidates) {
  const chosen = candidates.map((value) => String(value ?? '').trim()).find(Boolean) || '';
  return chosen.length > MAX_NAME_CHARS ? `${chosen.slice(0, MAX_NAME_CHARS)}…` : chosen;
}

/**
 * Lower-cased file extension, from the file name and falling back to the
 * document number. Exported with the two lists above so the pop-out classifies
 * a document exactly the way the report pipeline does — a file the report can
 * attach and a file the pop-out can show must never be different sets.
 */
export function extensionOf(document) {
  const name = document?.fileName || document?.documentNumber || '';
  const ext = String(name).split('.').pop();
  return ext ? ext.toLowerCase() : '';
}

function readAsDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('could not read the drawing file'));
    reader.readAsDataURL(blob);
  });
}

function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('drawing could not be decoded'));
    image.src = dataUrl;
  });
}

function mimeOf(dataUrl) {
  const match = /^data:([^;,]+)/.exec(dataUrl);
  return match ? match[1].toLowerCase() : '';
}

/**
 * Re-encode through a canvas, capped at MAX_LONG_EDGE.
 *
 * The source is a data URL, so the canvas is never tainted — the CORS question
 * was already settled by the fetch that produced it.
 */
function reencode(image, longEdge) {
  const scale = Math.min(1, longEdge / Math.max(image.naturalWidth, image.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const ctx = canvas.getContext('2d');
  // JPEG has no alpha; without this a transparent drawing turns black.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  return {
    dataUrl: canvas.toDataURL('image/jpeg', REENCODE_QUALITY),
    format: 'JPEG',
    width: canvas.width,
    height: canvas.height,
  };
}

/** Decode a fetched raster image, re-encoding only when it has to. */
async function decodeRasterImage(blob) {
  const dataUrl = await readAsDataUrl(blob);
  const image = await loadImage(dataUrl);
  const format = PASSTHROUGH_MIME[mimeOf(dataUrl)];
  const oversized = Math.max(image.naturalWidth, image.naturalHeight) > MAX_LONG_EDGE;

  if (format && !oversized) {
    return {
      dataUrl, format, width: image.naturalWidth, height: image.naturalHeight, reencoded: false,
    };
  }
  return { ...reencode(image, MAX_LONG_EDGE), reencoded: true };
}

/**
 * Rasterise page 1 of a PDF, or null when it is not a single-page document.
 *
 * `document` is NOT shadowed in here — the global is needed for the canvas, and
 * `resolveReferenceDrawing` takes a parameter of that name. Keeping the canvas
 * work in its own function is what makes that safe.
 *
 * Everything pdf.js allocates is released in `finally`: a parsed document holds
 * font and image caches well beyond the canvas, and the report can be reopened
 * any number of times in one session.
 */
async function rasterizeFirstPdfPage(blob, loadPdfjsLibrary) {
  // WE ARE THE SECOND pdf.js ON THIS PAGE. public/index.html:21 loads pdf.js
  // 2.4.456 from a CDN into `window.pdfjsLib` on every page load, and
  // forgeService.js:88 uses it for document thumbnails. That build resolves its
  // worker via `getMainThreadWorkerMessageHandler()`, which reads the very
  // `globalThis.pdfjsWorker` slot our v3 worker entry writes — and that lookup
  // outranks its own `workerSrc`. Leaving the global set makes v2 hand its
  // `apiVersion: "2.4.456"` to a 3.11.174 worker, which throws a version
  // mismatch that forgeService.js:8053 swallows via `.catch(debugError)`. The
  // visible symptom is a permanently blank PDF thumbnail in forgeCard.vue and
  // linkPreviewPopup.vue, with nothing in the console tying it back to here.
  // So: borrow both globals for the duration of this render, then hand them back.
  const prevWorker = globalThis.pdfjsWorker;
  const prevLib = globalThis.pdfjsLib;

  const pdfjs = await loadPdfjsLibrary();
  // Re-install on every render: the first call sets this via the module side
  // effect, later calls need it put back because our own `finally` removed it.
  if (v3WorkerHandle) globalThis.pdfjsWorker = v3WorkerHandle;

  const task = pdfjs.getDocument({ data: new Uint8Array(await blob.arrayBuffer()) });
  let pdf = null;
  let page = null;

  try {
    pdf = await task.promise;
    if (pdf.numPages !== 1) {
      debugLog('[referenceDrawing] skipped —', DRAWING_SKIP.MULTI_PAGE, { pages: pdf.numPages });
      return null;
    }

    page = await pdf.getPage(1);
    const unscaled = page.getViewport({ scale: 1 });
    const scale = PDF_TARGET_LONG_EDGE / Math.max(unscaled.width, unscaled.height);
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(viewport.width));
    canvas.height = Math.max(1, Math.round(viewport.height));
    const ctx = canvas.getContext('2d');
    // A PDF page has no background of its own. Without this the JPEG encoder
    // turns every unpainted pixel black and the drawing arrives inverted.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvasContext: ctx, viewport }).promise;

    return {
      dataUrl: canvas.toDataURL('image/jpeg', PDF_QUALITY),
      format: 'JPEG',
      width: canvas.width,
      height: canvas.height,
      reencoded: true,
      pages: 1,
    };
  } finally {
    try {
      page?.cleanup?.();
      await pdf?.destroy?.();
    } catch (err) {
      debugWarn('[referenceDrawing] pdf.js cleanup failed', err);
    }
    // AFTER destroy(), never before: tearing the document down still talks to
    // the worker, so the handler has to survive until cleanup has finished.
    restoreGlobal('pdfjsWorker', prevWorker);
    restoreGlobal('pdfjsLib', prevLib);
  }
}

/**
 * Resolve the selected reference document into a printable page, or null.
 *
 * NULL IS A NORMAL OUTCOME, not an error: no selection, an Office file, a
 * multi-page PDF, or a file we could not fetch all return null with a logged
 * reason. The report simply has no drawing page.
 *
 * @param {object|null} document A linked document from `state.linkedDocuments`.
 * @param {string} [name] Display name for the caption.
 * @param {{loadPdfjs?: Function}} [options] Test seam for the pdf.js boundary;
 *   production never passes it, so the lazy import stays the only path.
 * @returns {Promise<{dataUrl,format,width,height,name}|null>}
 */
export async function resolveReferenceDrawing(document, name = '', options = {}) {
  if (!document) {
    debugLog('[referenceDrawing] skipped —', DRAWING_SKIP.NONE_SELECTED);
    return null;
  }

  const extension = extensionOf(document);
  const isPdf = extension === PDF_EXTENSION;
  if (!isPdf && !IMAGE_EXTENSIONS.includes(extension)) {
    debugLog('[referenceDrawing] skipped —', DRAWING_SKIP.UNSUPPORTED_FORMAT, { extension });
    return null;
  }

  const source = document.storageReference || document.url || null;
  if (!source) {
    debugLog('[referenceDrawing] skipped —', DRAWING_SKIP.NO_SOURCE);
    return null;
  }

  try {
    // A cross-origin SAS URL: this fetch is the CORS gate. If Azure does not
    // allow this origin it throws here, which is exactly the skip path.
    const response = await fetch(source);
    if (!response.ok) throw new Error(`fetch returned ${response.status}`);
    const blob = await response.blob();

    const resolved = isPdf
      ? await rasterizeFirstPdfPage(blob, options.loadPdfjs || loadPdfjs)
      : await decodeRasterImage(blob);

    // A multi-page PDF has already logged its own reason.
    if (!resolved) return null;

    debugLog('[referenceDrawing] resolved', {
      name,
      source: isPdf ? 'pdf page 1' : 'raster image',
      format: resolved.format,
      size: `${resolved.width}x${resolved.height}`,
      reencoded: resolved.reencoded,
    });
    return {
      dataUrl: resolved.dataUrl,
      format: resolved.format,
      width: resolved.width,
      height: resolved.height,
      name: safeName(name, document.fileName, 'Reference drawing'),
    };
  } catch (err) {
    debugWarn('[referenceDrawing] skipped —', DRAWING_SKIP.UNREADABLE, err);
    return null;
  }
}

export default resolveReferenceDrawing;
